import { PairedCostVersion } from '@aloft/toolkit-shared';
import { Flex } from '@aloft/toolkit-sui';

import { PairedCostAdjustmentSummaryBreakdownDetailModal } from './BreakdownDetailModal/PairedCostAdjustmentSummaryBreakdownDetailModal.js';
import { PairedCostAdjustmentSummaryModalContextProvider } from './BreakdownDetailModal/PairedCostAdjustmentSummaryModalContextProvider.js';
import { PairedCostAdjustmentSummaryTableHeaderRow } from './PairedCostAdjustmentSummaryTableHeaderRow.js';
import { PairedCostAdjustmentSummaryTableMainDwellingRows } from './PairedCostAdjustmentSummaryTableMainDwellingRows.js';
import { PairedCostAdjustmentSummaryTablePropertyHeader } from './PairedCostAdjustmentSummaryTablePropertyHeader.js';
import { PairedCostAdjustmentSummaryTableRollupRow } from './PairedCostAdjustmentSummaryTableRollupRow.js';
import { PairedCostAdjustmentSummaryTableRow } from './PairedCostAdjustmentSummaryTableRow.js';
import { PairedCostAdjustmentSummaryTableSiteRows } from './PairedCostAdjustmentSummaryTableSiteRows.js';
import { PairedCostAdjustmentSummaryTableSubheaderRow } from './PairedCostAdjustmentSummaryTableSubeaderRow.js';
import {
  depreciatedCostAdjustmentsAfterMainDwelling,
  depreciatedCostAdjustmentsBeforeMainDwelling,
  marketAdjustments,
} from './consts.js';
import { AllPairedCostAdjustmentSummaryTypes, PairedCostAdjustmentSummaryProps } from './types.js';
import { isValidForVersion } from './utils/isValidForVersion.js';

import './PairedCostAdjustmentSummary.scss';

export const PairedCostAdjustmentSummaryTable = (
  props: PairedCostAdjustmentSummaryProps & {
    pairedCostVersion: PairedCostVersion;
    renderModalContent: (adjustmentType: AllPairedCostAdjustmentSummaryTypes) => JSX.Element | null;
  },
) => (
  <PairedCostAdjustmentSummaryModalContextProvider>
    <Flex
      className="paired-cost-adjustment-summary"
      flexDirection="column"
      maxWidth="100%"
      marginTop=".5rem"
      overflow="auto"
      flexGrow={0}
    >
      <table>
        <thead>
          <PairedCostAdjustmentSummaryTablePropertyHeader {...props} />
          <PairedCostAdjustmentSummaryTableHeaderRow {...props} />
        </thead>
        <tbody>
          <PairedCostAdjustmentSummaryTableSubheaderRow
            {...props}
            title={
              props.pairedCostVersion === 'legacy'
                ? 'Market Adjustments'
                : 'Date of Sale Adjustments'
            }
          />
          {marketAdjustments.map((row, idx) => (
            <PairedCostAdjustmentSummaryTableRow
              key={`table-row-${row}-${idx}`}
              {...props}
              row={row}
            />
          ))}
          <PairedCostAdjustmentSummaryTableSubheaderRow
            {...props}
            title="Site Extraction Adjustments"
          />
          <PairedCostAdjustmentSummaryTableSiteRows {...props} />
          <PairedCostAdjustmentSummaryTableSubheaderRow
            {...props}
            title="Depreciated Cost Adjustments"
          />
          {depreciatedCostAdjustmentsBeforeMainDwelling.map((row, idx) => (
            <PairedCostAdjustmentSummaryTableRow
              key={`table-row-${row}-${idx}`}
              {...props}
              row={row}
            />
          ))}
          <PairedCostAdjustmentSummaryTableMainDwellingRows {...props} />
          {depreciatedCostAdjustmentsAfterMainDwelling
            .filter((row) => isValidForVersion(props.pairedCostVersion, row.path))
            .map((row, idx) => (
              <PairedCostAdjustmentSummaryTableRow
                key={`table-row-${row}-${idx}`}
                {...props}
                row={row}
              />
            ))}
          <PairedCostAdjustmentSummaryTableRollupRow
            {...props}
            label="Net Adj. Total"
            path="netAdjustment"
            includeTopBorder
          />
          <PairedCostAdjustmentSummaryTableRollupRow
            {...props}
            label="Net Adj. %"
            path="netAdjustmentPct"
          />
          <PairedCostAdjustmentSummaryTableRollupRow
            {...props}
            label="Gross Adj. %"
            path="grossAdjustmentPct"
          />
          <PairedCostAdjustmentSummaryTableRollupRow
            {...props}
            label="Adjusted SP"
            path="adjustedSalePrice"
            includeTopBorder
            bold
          />
        </tbody>
      </table>
    </Flex>
    <PairedCostAdjustmentSummaryBreakdownDetailModal
      renderModalContent={props.renderModalContent}
    />
  </PairedCostAdjustmentSummaryModalContextProvider>
);
